import { queryOptions, useQuery } from '@tanstack/react-query';
import flatMap from 'lodash/flatMap';
import uniq from 'lodash/uniq';

import type { DateRange } from '@cast/shared-types';
import { NamespaceFiltersRequest } from '@cast/types';

import { apiClient } from '@cast/infrastructure-api';

const NAMESPACE_FILTERS_QUERY_KEY = 'namespace-filters';

const getNamespacesQueryKey = ({
  startTime,
  endTime,
  filter,
}: { startTime?: string; endTime?: string; filter?: string } = {}) =>
  [NAMESPACE_FILTERS_QUERY_KEY, startTime, endTime, filter] as const;

type NamespaceQuery = {
  clusterIds: string[];
  range: DateRange;
  stepSeconds?: number;
  enabled?: boolean;
};

const getNamespacesQueryOptions = ({
  clusterIds,
  range: [from, to],
  enabled = true,
}: NamespaceQuery) => {
  const filter: NamespaceFiltersRequest = {
    clusterIds,
  };

  return queryOptions({
    queryKey: getNamespacesQueryKey({
      startTime: from.toISOString(),
      endTime: to.toISOString(),
      filter: JSON.stringify(filter),
    }),
    queryFn: async () => {
      const { data } = await apiClient.costReport.getNamespaces(
        {
          startTime: from.toISOString(),
          endTime: to.toISOString(),
          namespaceFilters: filter,
        },
        { timeout: 30_000 }
      );

      const namespaces =
        data.clusterNamespaces?.map((item) => item.namespaces || []) || [];

      return uniq(flatMap(namespaces));
    },
    enabled,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });
};

export const useNamespacesQuery = (props: NamespaceQuery) => {
  const { data, isLoading, error, refetch } = useQuery(
    getNamespacesQueryOptions(props)
  );

  return {
    namespaces: data,
    isLoading,
    error,
    refetch,
  };
};
